import React from "react";
import styles from "../../../../assets/styles/components/Suggestion.module.scss";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------
export type ReplaceImageAssetSuggestion = {
	asset_id: string;
	campaign_id?: string;
	campaign_name?: string;
	ad_group_id?: string;
	field_type?: string;
	ad_group_name: string;
	before_image_url: string; // URL for the poor performing image
	performance_status: string; // e.g. "LOW"
	reason?: string;
};

export type ReplaceImageAssetUploadValue = {
	fileName: string;
	dataUrl: string;
};

const ASSET_SPECS = [
	{ ratio: 1.91, minW: 600, minH: 314, suffix: "landscape", fieldType: "MARKETING_IMAGE" },
	{ ratio: 1.0, minW: 300, minH: 300, suffix: "square", fieldType: "SQUARE_MARKETING_IMAGE" },
	{ ratio: 0.8, minW: 400, minH: 600, suffix: "portrait", fieldType: "PORTRAIT_MARKETING_IMAGE" },
];

const getSpecForFieldType = (fieldType?: string) => {
	const normalized = String(fieldType || "").toUpperCase().trim();
	return ASSET_SPECS.find((s) => s.fieldType === normalized) || ASSET_SPECS[0];
};

const loadImage = (file: File): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const url = URL.createObjectURL(file);
		const img = new Image();
		img.onload = () => {
			URL.revokeObjectURL(url);
			resolve(img);
		};
		img.onerror = () => {
			URL.revokeObjectURL(url);
			reject(new Error("画像の読み込みに失敗しました"));
		};
		img.src = url;
	});

const buildCroppedDataUrl = async (
	file: File,
	fieldType?: string,
): Promise<ReplaceImageAssetUploadValue> => {
	const img = await loadImage(file);
	const srcW = img.naturalWidth;
	const srcH = img.naturalHeight;
	const srcRatio = srcW / srcH;

	const spec = getSpecForFieldType(fieldType);

	let cropW = srcW;
	let cropH = srcH;
	let sx = 0;
	let sy = 0;

	if (srcRatio > spec.ratio) {
		cropW = srcH * spec.ratio;
		sx = (srcW - cropW) / 2;
	} else if (srcRatio < spec.ratio) {
		cropH = srcW / spec.ratio;
		sy = (srcH - cropH) / 2;
	}

	const scale = Math.max(spec.minW / cropW, spec.minH / cropH, 1);
	const outW = Math.round(cropW * scale);
	const outH = Math.round(cropH * scale);

	const canvas = document.createElement("canvas");
	canvas.width = outW;
	canvas.height = outH;
	const ctx = canvas.getContext("2d");
	if (!ctx) {
		throw new Error("画像変換に失敗しました");
	}
	ctx.drawImage(img, sx, sy, cropW, cropH, 0, 0, outW, outH);

	const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
	const fileName = `${file.name.replace(/\.[^.]+$/, "")}_${spec.suffix}.jpg`;
	return { fileName, dataUrl };
};

type Props = {
	assets: ReplaceImageAssetSuggestion[];
	formData: Record<string, ReplaceImageAssetUploadValue | null>;
	onChange: (id: string, fileData: ReplaceImageAssetUploadValue | null) => void;
	platform?: "GOOGLE" | "META";
};

// -----------------------------------------------------------------------------
// Component: ReplaceImageAssetForm
// -----------------------------------------------------------------------------
const ReplaceImageAssetForm: React.FC<Props> = ({ assets, formData, onChange, platform = "GOOGLE" }) => {
	const getRequirementLabel = (fieldType?: string) => {
		if (platform === "META") {
			return "Metaの推奨比率に合わせた画像を選択してください";
		}
		return fieldType === "MARKETING_IMAGE"
			? "1.91:1 (横長)"
			: fieldType === "SQUARE_MARKETING_IMAGE"
				? "1:1 (正方形)"
				: fieldType === "PORTRAIT_MARKETING_IMAGE"
					? "4:5 (縦長)"
					: "1.91:1 (横長 / 不明時の既定)";
	};
	return (
		<div className={styles.imageAssetFormContainer}>
			{assets.map((as) => {
				const replacementFile = formData[as.asset_id];

				return (
					<section key={as.asset_id} className={styles.campaignCard}>
						<div className={styles.campaignName}>{as.ad_group_name}</div>

						{as.reason && (
							<div className={styles.reasonBar}>
								<strong>Aibex提案理由:</strong> {as.reason}
							</div>
						)}

						<div className={styles.suggestionBox}>
							{/* Before */}
							<div className={styles.beforePanel}>
								<div className={styles.panelTitle}>現在の設定</div>
								<div className={styles.imageDisplayBox}>
									{as.before_image_url ? (
										<img src={as.before_image_url} alt="Current Asset" className={styles.previewImage} />
									) : (
										<div className={styles.imagePlaceholder}>No Image</div>
									)}
									<div className={`${styles.statusBadge} ${styles.bgOff} ${styles.imageStatusBadgeTopMargin}`}>
										パフォーマンス: {as.performance_status}
									</div>
								</div>
							</div>

							{/* Arrow */}
							<div className={styles.arrowIcon}>→</div>

							{/* After: Upload Replacement UI */}
							<div className={styles.afterPanel}>
								<div className={styles.panelTitle}>新しい画像へ差し替え</div>
								<div className={styles.reasonBar}>
									<strong>必要比率:</strong>{" "}
									{getRequirementLabel(as.field_type)}
								</div>
								<div className={styles.uploadArea}>
									{replacementFile ? (
										<div className={styles.uploadedPreview}>
											<span>{replacementFile.fileName}</span>
											<button type="button" onClick={() => onChange(as.asset_id, null)}>
												削除
											</button>
										</div>
									) : (
										<label className={styles.uploadBox}>
											<input
												type="file"
												accept="image/*"
												className={styles.hiddenInput}
												onChange={async (e) => {
													if (e.target.files && e.target.files.length > 0) {
														const file = e.target.files[0];
														try {
															const converted = await buildCroppedDataUrl(file, as.field_type);
															onChange(as.asset_id, converted);
														} catch (err) {
															console.error("[ReplaceImageAssetForm] image convert failed", err);
															window.alert(err instanceof Error ? err.message : "画像変換に失敗しました");
														}
													}
												}}
											/>
											<div className={styles.uploadPlaceholder}>
												<span>クリックして差し替え画像をアップロード</span>
											</div>
										</label>
									)}
								</div>
							</div>
						</div>
					</section>
				);
			})}
		</div>
	);
};

export default ReplaceImageAssetForm;
